import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";

const EMPTY_FORM = {
  email: "",
  name: "",
  password: "",
  role: "user",
  is_active: true,
};

const ROLE_LABEL = {
  admin: "Administrador",
  user: "Usuario",
};

export default function UsersPanel() {
  const { token, user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);

  async function load() {
    const data = await api("/api/auth/users", { token });
    setUsers(data.users || []);
  }

  useEffect(() => {
    let cancelled = false;
    api("/api/auth/users", { token })
      .then((data) => {
        if (!cancelled) setUsers(data.users || []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  function startCreate() {
    setEditId(null);
    setForm(EMPTY_FORM);
    setError("");
    setMessage("");
    setShowForm(true);
  }

  function startEdit(u) {
    setEditId(u.id);
    setForm({
      email: u.email || "",
      name: u.name || "",
      password: "",
      role: u.role || "user",
      is_active: Boolean(u.is_active),
    });
    setError("");
    setMessage("");
    setShowForm(true);
    try {
      document.getElementById("users-form-anchor")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } catch {
      /* ignore */
    }
  }

  function cancelForm() {
    setEditId(null);
    setForm(EMPTY_FORM);
    setShowForm(false);
    setError("");
  }

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");
    try {
      if (editId) {
        const body = {
          email: form.email.trim(),
          name: form.name.trim(),
          role: form.role,
          is_active: form.is_active,
        };
        if (form.password.trim()) body.password = form.password;
        await api(`/api/auth/users/${editId}`, { method: "PUT", token, body });
        setMessage("Usuario actualizado.");
      } else {
        await api("/api/auth/users", {
          method: "POST",
          token,
          body: {
            email: form.email.trim(),
            name: form.name.trim(),
            password: form.password,
            role: form.role,
          },
        });
        setMessage("Usuario creado.");
        setForm(EMPTY_FORM);
      }
      await load();
      if (!editId) setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function removeUser(id) {
    if (!window.confirm("¿Borrar este usuario?")) return;
    setError("");
    try {
      await api(`/api/auth/users/${id}`, { method: "DELETE", token });
      if (editId === id) cancelForm();
      setMessage("Usuario borrado.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function toggleActive(u) {
    setError("");
    try {
      await api(`/api/auth/users/${u.id}`, {
        method: "PUT",
        token,
        body: { is_active: !u.is_active },
      });
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  const filteredUsers = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) => {
      const hay = [u.name, u.email, u.role, String(u.id)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(term);
    });
  }, [users, query]);

  const admins = users.filter((u) => u.role === "admin" && u.is_active).length;
  const formOpen = showForm || Boolean(editId);

  return (
    <div className="users-panel">
      <div className="users-hero">
        <div>
          <h2>Administración de usuarios</h2>
          <p className="muted">
            Crea cuentas, cambia roles y activa/desactiva acceso. {admins} admin
            {admins === 1 ? "" : "s"} activo{admins === 1 ? "" : "s"}.
          </p>
        </div>
        <button type="button" className="users-add-btn" onClick={startCreate}>
          + Nuevo usuario
        </button>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {message ? <p className="ok">{message}</p> : null}

      <div className="admin-toolbar">
        <label className="admin-search">
          <span className="sr-only">Buscar usuarios</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar nombre, email o rol…"
            inputMode="search"
          />
        </label>
        <p className="muted admin-toolbar-meta">
          {filteredUsers.length} / {users.length}
        </p>
      </div>

      <div id="users-form-anchor" />
      {formOpen ? (
        <form className="edit-form users-form" onSubmit={onSubmit}>
          <h3 className="full">{editId ? `Editar usuario #${editId}` : "Nuevo usuario"}</h3>
          <label>
            Nombre
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nombre visible"
              autoComplete="off"
            />
          </label>
          <label>
            Email
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              autoComplete="off"
            />
          </label>
          <label>
            Rol
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <option value="user">Usuario</option>
              <option value="admin">Administrador</option>
            </select>
          </label>
          <label>
            Contraseña{editId ? " (opcional)" : ""}
            <input
              type="password"
              required={!editId}
              minLength={editId ? undefined : 8}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              autoComplete="new-password"
              placeholder={editId ? "Nueva contraseña" : "Mínimo 8 caracteres"}
            />
          </label>
          {editId ? (
            <label className="users-active-check">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              Cuenta activa
            </label>
          ) : null}
          <div className="full form-actions">
            <button type="submit" disabled={busy}>
              {busy ? "Guardando…" : editId ? "Guardar" : "Crear"}
            </button>
            <button type="button" className="btn-ghost" onClick={cancelForm}>
              Cancelar
            </button>
          </div>
        </form>
      ) : null}

      {/* Cards (móvil / tablet) */}
      <div className="users-cards" aria-label="Lista de usuarios">
        {filteredUsers.length ? (
          filteredUsers.map((u) => (
            <article key={u.id} className="users-card">
              <div className="users-card-top">
                <div className="users-card-id">#{u.id}</div>
                <span className={`badge badge-${u.is_active ? "ready" : "failed"}`}>
                  {u.is_active ? "activo" : "inactivo"}
                </span>
              </div>
              <h3 className="users-card-name">
                {u.name || "Sin nombre"}
                {me?.id === u.id ? <span className="you-pill">tú</span> : null}
              </h3>
              <p className="users-card-email">{u.email}</p>
              <span className={`role-pill role-${u.role}`}>
                {ROLE_LABEL[u.role] || u.role}
              </span>
              <div className="users-card-actions">
                <button type="button" onClick={() => startEdit(u)}>
                  Editar
                </button>
                <button type="button" onClick={() => toggleActive(u)}>
                  {u.is_active ? "Desactivar" : "Activar"}
                </button>
                <button
                  type="button"
                  className="danger"
                  disabled={me?.id === u.id}
                  onClick={() => removeUser(u.id)}
                >
                  Borrar
                </button>
              </div>
            </article>
          ))
        ) : (
          <p className="muted users-empty">No hay usuarios con ese filtro.</p>
        )}
      </div>

      {/* Tabla (escritorio) */}
      <div className="admin-table-wrap users-table-wrap">
        <table className="admin-table admin-table-striped users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u, index) => (
              <tr
                key={u.id}
                className="admin-row admin-stagger"
                style={{ "--i": index }}
              >
                <td data-label="ID">{u.id}</td>
                <td data-label="Nombre">{u.name || "—"}</td>
                <td data-label="Email">
                  {u.email}
                  {me?.id === u.id ? <span className="you-pill">tú</span> : null}
                </td>
                <td data-label="Rol">
                  <span className={`role-pill role-${u.role}`}>
                    {ROLE_LABEL[u.role] || u.role}
                  </span>
                </td>
                <td data-label="Estado">
                  <span className={`badge badge-${u.is_active ? "ready" : "failed"}`}>
                    {u.is_active ? "activo" : "inactivo"}
                  </span>
                </td>
                <td className="actions">
                  <button type="button" onClick={() => startEdit(u)}>
                    Editar
                  </button>
                  <button type="button" onClick={() => toggleActive(u)}>
                    {u.is_active ? "Desactivar" : "Activar"}
                  </button>
                  <button
                    type="button"
                    className="danger"
                    disabled={me?.id === u.id}
                    onClick={() => removeUser(u.id)}
                  >
                    Borrar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
